import React, { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import { Input } from 'shadcn/components/ui/Input';
import { Popover, PopoverContent, PopoverTrigger } from 'shadcn/components/ui/Popover';
import { cn } from 'shadcn/lib/utils';

const DEBOUNCE_MS = 300;

/**
 * Popover column filter with search icon trigger for SubstitutionRulesTable headers.
 * Sync pattern avoids keystroke loss when filter recalc echoes stale filterValue (see TextColumnFilter).
 *
 * @param {Object} props
 * @param {import('@tanstack/react-table').Column<Record<string, unknown>, unknown>} props.column
 * @param {string} [props.placeholder]
 * @param {string} [props['aria-label']] - Accessible name for the search input
 * @param {string} [props.triggerAriaLabel] - Accessible name for the search icon trigger
 */
export function SubstitutionRulesColumnTextFilter({
    'aria-label': ariaLabel,
    column,
    placeholder,
    triggerAriaLabel,
}) {
    const filterValue = column.getFilterValue();
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState(filterValue ?? '');
    const setFilterValueRef = useRef(column.setFilterValue);
    const lastPushedRef = useRef(filterValue ?? '');
    const debounceTimerRef = useRef(null);
    const valueRef = useRef(value);
    const inputRef = useRef(null);
    valueRef.current = value;

    const hasActiveFilter = String(filterValue ?? '').trim().length > 0;

    useEffect(() => {
        setFilterValueRef.current = column.setFilterValue;
    }, [column.setFilterValue]);

    useEffect(() => {
        const incoming = filterValue ?? '';
        const local = valueRef.current;
        if (incoming === local) return;
        if (local !== lastPushedRef.current) return;
        setValue(incoming);
        lastPushedRef.current = incoming;
    }, [filterValue]);

    useEffect(() => {
        let frameId = null;

        if (open) {
            frameId = requestAnimationFrame(() => {
                inputRef.current?.focus();
            });
        }

        return () => {
            if (frameId != null) {
                cancelAnimationFrame(frameId);
            }
        };
    }, [open]);

    useEffect(
        () => () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        },
        [],
    );

    const pushFilterValue = (next) => {
        const nextPushed = next;
        if (nextPushed === lastPushedRef.current) return;
        lastPushedRef.current = nextPushed;
        setFilterValueRef.current(nextPushed || undefined);
    };

    const handleChange = (event) => {
        const next = event.target.value;
        setValue(next);

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            debounceTimerRef.current = null;
            pushFilterValue(next);
        }, DEBOUNCE_MS);
    };

    const handleClear = () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        setValue('');
        pushFilterValue('');
        inputRef.current?.focus();
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="ghost"
                    size="iconSm"
                    className={cn(
                        'relative size-7 shrink-0 text-muted-foreground hover:text-foreground',
                        hasActiveFilter &&
                            'bg-primary/15 text-primary ring-2 ring-primary/35 hover:bg-primary/20 hover:text-primary',
                    )}
                    aria-label={triggerAriaLabel ?? ariaLabel}
                    aria-pressed={hasActiveFilter}
                    data-filter-active={hasActiveFilter ? 'true' : 'false'}
                    onClick={(event) => event.stopPropagation()}
                >
                    <Search className="size-3.5" aria-hidden />
                    {hasActiveFilter ? (
                        <span
                            className="absolute right-0.5 top-0.5 size-1.5 rounded-full bg-primary ring-1 ring-background"
                            aria-hidden
                        />
                    ) : null}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="start" onClick={(event) => event.stopPropagation()}>
                <div className="relative">
                    <Input
                        ref={inputRef}
                        type="text"
                        inputMode="search"
                        enterKeyHint="search"
                        role="searchbox"
                        autoComplete="off"
                        aria-label={ariaLabel}
                        placeholder={placeholder}
                        value={value}
                        onChange={handleChange}
                        className="h-8 pr-8 text-xs"
                    />
                    {value ? (
                        <Button
                            type="button"
                            variant="ghost"
                            size="iconSm"
                            className="absolute right-0.5 top-1/2 size-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            onClick={handleClear}
                            aria-label={`Clear ${ariaLabel ?? 'filter'}`}
                        >
                            <X className="size-3.5" aria-hidden />
                        </Button>
                    ) : null}
                </div>
            </PopoverContent>
        </Popover>
    );
}

SubstitutionRulesColumnTextFilter.displayName = 'SubstitutionRulesColumnTextFilter';
